/**
 * PoseErrorClassifier
 *
 * Classifies pose problems into semantic, user-facing categories with
 * messages suitable for both on-screen display and voice prompts.
 *
 * Inputs: COCO-17 keypoints in image pixel space, view, image dimensions,
 * and (optionally) the 3D body rotation angle from MediaPipe worldLandmarks.
 *
 * Thresholds were picked by examining the fixture distributions in
 * public/pose_samples.json (see analysis in session notes).
 */

// COCO-17 landmark indices
const NOSE = 0;
const L_SHOULDER = 5;
const R_SHOULDER = 6;
const L_ELBOW = 7;
const R_ELBOW = 8;
const L_WRIST = 9;
const R_WRIST = 10;
const L_HIP = 11;
const R_HIP = 12;
const L_KNEE = 13;
const R_KNEE = 14;
const L_ANKLE = 15;
const R_ANKLE = 16;

export type PoseErrorId =
  | "wrong_orientation"
  | "bad_detection"
  | "collapsed_detection"
  | "rotation"
  | "arm_bent_left"
  | "arm_bent_right"
  | "arm_raised_left"
  | "arm_raised_right"
  | "arms_asymmetric"
  | "legs_too_close"
  | "legs_too_wide"
  | "torso_tilted"
  | "off_centre"
  | "clipped_top"
  | "clipped_bottom"
  | "clipped_side"
  | "too_far"
  | "too_close"
  | "arm_outside_side"
  | "camera_too_high"
  | "camera_too_low";

export type Severity = "warning" | "error";

export interface PoseError {
  id: PoseErrorId;
  severity: Severity;
  /** Short label for UI pills */
  label: string;
  /** Friendly message suitable for voice / selfie mode */
  message: string;
  /** Optional numeric value for debugging */
  value?: number;
}

export interface ClassifyInput {
  cocoKpPx: [number, number][];
  view: "front" | "right";
  imageWidth: number;
  imageHeight: number;
  /** l_elbow_ang (degrees, 180° = straight) */
  lElbowAng?: number;
  /** r_elbow_ang */
  rElbowAng?: number;
  /** torso_tilt_deg (0 = upright) */
  torsoTiltDeg?: number;
  /** Y-axis body rotation in degrees from MediaPipe worldLandmarks (0 = facing camera) */
  bodyRotationDeg?: number;
  /** shoulder_z − ankle_z in metres (MediaPipe world). < −0.5 → shoulders much closer than ankles → camera too high/looking down */
  cameraElevationM?: number;
}

// ── Thresholds (chosen from fixture analysis) ──────────────────────────────
const COLLAPSED_KP_PX = 5;       // elbows + wrists < 5px apart → collapsed
const ROT_FRONT_DEG = 7.5;
const ELBOW_STRAIGHT_MIN_DEG = 160;
// Side-view threshold is lower: a straight arm in profile measures 148–158°
// due to perspective collapse. We only flag clearly bent arms (≤140°).
const ELBOW_STRAIGHT_MIN_DEG_SIDE = 140;
const WRIST_REL_SHOULDER_RAISED_MAX = 0.2; // wrist_rel_sh < 0.2 → raised
const ARM_ASYMMETRY_MAX = 0.35; // |lWr - rWr| relative to trunk
const LEG_NARROW_MAX = 0.75; // ankle_dx / hip_w
const LEG_WIDE_MAX = 2.5;
const TORSO_TILT_MAX_DEG = 8;
const OFF_CENTRE_FRAC = 0.12;
const MARGIN_MIN_FRAC = 0.06;  // min fraction of image dimension between keypoint and edge
const BODY_TOO_CLOSE_FRAC = 0.90; // body_h / image_h
const BODY_TOO_FAR_FRAC = 0.35;
// Side-view arm-outside-body (matches V2 armInsideBody thresholds in poseGate.generated.ts)
// Error fires when arm sticks OUT beyond these limits — arm should stay close to the trunk.
const ARM_OUTSIDE_MAX_WRIST = 0.25; // dxWrist / trunkLen > this → wrist too far from trunk
const ARM_OUTSIDE_MAX_ELBOW = 0.15; // dxElbow / trunkLen > this → elbow too far from trunk
// shoulder_z − ankle_z < threshold: shoulders much closer to camera than ankles → camera looking DOWN (too high)
const CAMERA_TOO_HIGH_M      = -0.5;   // front view threshold
const CAMERA_TOO_HIGH_M_SIDE = -0.35;  // side view: attenuated signal, lower threshold
// shoulder_z − ankle_z > threshold: ankles much closer to camera than shoulders → camera looking UP (too low/floor)
const CAMERA_TOO_LOW_M       =  0.15;  // front view threshold (p99 = 0.145)
const CAMERA_TOO_LOW_M_SIDE  =  0.20;  // side view threshold (p99 = 0.188)

/**
 * Classify a pose into semantic errors.
 *
 * Returns an empty array when the pose is considered correct.
 */
export function classifyPoseErrors(input: ClassifyInput): PoseError[] {
  const { cocoKpPx: kp, view, imageWidth: W, imageHeight: H } = input;
  const errors: PoseError[] = [];

  if (!kp || kp.length < 17 || W <= 0 || H <= 0) return errors;

  // ── Image orientation ────────────────────────────────────────────────────
  // Must be portrait for a standing full-body shot.
  if (W > H) {
    errors.push({
      id: "wrong_orientation",
      severity: "error",
      label: "Wrong orientation",
      message: "Please hold your phone upright (portrait mode) for body scanning.",
      value: +(W / H).toFixed(2),
    });
  }

  // ── Anatomically impossible detection → bail early ───────────────────────
  // If hips appear above shoulders OR an ankle appears above its knee, the
  // pose detector has failed completely and the remaining checks are noise.
  {
    const avgShoY = (kp[L_SHOULDER][1] + kp[R_SHOULDER][1]) / 2;
    const avgHipY = (kp[L_HIP][1] + kp[R_HIP][1]) / 2;
    const lAnkAboveKne = kp[L_ANKLE][1] < kp[L_KNEE][1];
    const rAnkAboveKne = kp[R_ANKLE][1] < kp[R_KNEE][1];
    if (avgHipY < avgShoY || lAnkAboveKne || rAnkAboveKne) {
      errors.push({
        id: "bad_detection",
        severity: "error",
        label: "Detection failed",
        message: "Body not detected correctly — make sure you're fully in frame against a plain background.",
      });
      return errors;
    }
  }

  // ── Collapsed keypoints (extreme occlusion / misdetection) ───────────────
  // Both left and right elbows at the same pixel means the model collapsed
  // left/right sides together — the image is unusable.
  {
    const elbowDist = Math.hypot(
      kp[L_ELBOW][0] - kp[R_ELBOW][0],
      kp[L_ELBOW][1] - kp[R_ELBOW][1]
    );
    const wristDist = Math.hypot(
      kp[L_WRIST][0] - kp[R_WRIST][0],
      kp[L_WRIST][1] - kp[R_WRIST][1]
    );
    if (elbowDist < COLLAPSED_KP_PX && wristDist < COLLAPSED_KP_PX) {
      errors.push({
        id: "collapsed_detection",
        severity: "error",
        label: "Body obscured",
        message: "Your body isn't clearly visible — check the lighting and make sure nothing is covering you.",
        value: +elbowDist.toFixed(1),
      });
    }
  }

  // ── Clipping / framing (checked first — supersedes pose checks) ─────────
  const xs = kp.map((p) => p[0]);
  const ys = kp.map((p) => p[1]);
  const bboxX0 = Math.min(...xs);
  const bboxX1 = Math.max(...xs);
  const bboxY0 = Math.min(...ys);
  const bboxY1 = Math.max(...ys);
  const bboxCx = (bboxX0 + bboxX1) / 2;
  const bodyH = bboxY1 - bboxY0;

  const topMargin = bboxY0 / H;
  const botMargin = (H - bboxY1) / H;
  const leftMargin = bboxX0 / W;
  const rightMargin = (W - bboxX1) / W;
  const offX = (bboxCx - W / 2) / W;
  const bodyHRatio = bodyH / H;

  if (topMargin < MARGIN_MIN_FRAC) {
    errors.push({
      id: "clipped_top",
      severity: "error",
      label: "Head clipped",
      message: "I can't see the top of your head — step back or aim the camera up.",
      value: +topMargin.toFixed(3),
    });
  }
  if (botMargin < MARGIN_MIN_FRAC) {
    errors.push({
      id: "clipped_bottom",
      severity: "error",
      label: "Feet clipped",
      message: "I can't see your feet — step back or aim the camera down.",
      value: +botMargin.toFixed(3),
    });
  }
  if (leftMargin < MARGIN_MIN_FRAC || rightMargin < MARGIN_MIN_FRAC) {
    errors.push({
      id: "clipped_side",
      severity: "error",
      label: "Body cut off",
      message: "Your arms or body are too close to the edge — centre yourself and keep arms slightly in from the sides.",
      value: +Math.min(leftMargin, rightMargin).toFixed(3),
    });
  }
  if (bodyHRatio > BODY_TOO_CLOSE_FRAC) {
    errors.push({
      id: "too_close",
      severity: "warning",
      label: "Too close",
      message: "You're too close to the camera — step back a little.",
      value: +bodyHRatio.toFixed(2),
    });
  } else if (bodyHRatio < BODY_TOO_FAR_FRAC) {
    errors.push({
      id: "too_far",
      severity: "warning",
      label: "Too far",
      message: "You're too far from the camera — step a little closer.",
      value: +bodyHRatio.toFixed(2),
    });
  }
  if (Math.abs(offX) > OFF_CENTRE_FRAC) {
    errors.push({
      id: "off_centre",
      severity: "warning",
      label: "Off centre",
      message:
        offX > 0
          ? "Move a little to your left to centre yourself."
          : "Move a little to your right to centre yourself.",
      value: +offX.toFixed(2),
    });
  }

  // ── Y-axis body rotation (front view only; needs MediaPipe 3D input) ────
  if (view === "front" && input.bodyRotationDeg !== undefined) {
    if (input.bodyRotationDeg > ROT_FRONT_DEG) {
      errors.push({
        id: "rotation",
        severity: "warning",
        label: "Rotated",
        message: "Turn slightly to face the camera straight on.",
        value: +input.bodyRotationDeg.toFixed(1),
      });
    }
  }

  // ── Torso tilt ──────────────────────────────────────────────────────────
  if (input.torsoTiltDeg !== undefined) {
    const tilt = Math.abs(input.torsoTiltDeg);
    if (tilt > TORSO_TILT_MAX_DEG) {
      errors.push({
        id: "torso_tilted",
        severity: "warning",
        label: "Torso tilted",
        message: "Stand upright — don't lean to one side.",
        value: +tilt.toFixed(1),
      });
    }
  }

  // ── Arm bend ────────────────────────────────────────────────────────────
  // On a right-side view the LEFT arm is fully occluded (far arm). MediaPipe
  // places it at the same pixel as the right arm, producing meaningless elbow
  // angles as low as 78° on perfectly straight arms. Only check the LEFT arm
  // on front view.
  //
  // The RIGHT (near/visible) arm in a side profile also reads low due to
  // perspective foreshortening — a straight arm measures 148–158°. We use a
  // lower threshold (140°) for side view to only catch genuinely bent arms.
  const checkLeftBend  = view === "front";
  const bendThreshold  = view === "front" ? ELBOW_STRAIGHT_MIN_DEG : ELBOW_STRAIGHT_MIN_DEG_SIDE;
  if (checkLeftBend && input.lElbowAng !== undefined && input.lElbowAng < ELBOW_STRAIGHT_MIN_DEG) {
    errors.push({
      id: "arm_bent_left",
      severity: "error",
      label: "L arm bent",
      message: "Straighten your left arm — keep it straight, angled slightly out from your body.",
      value: +input.lElbowAng.toFixed(0),
    });
  }
  if (input.rElbowAng !== undefined && input.rElbowAng < bendThreshold) {
    errors.push({
      id: "arm_bent_right",
      severity: "error",
      label: "R arm bent",
      message: "Straighten your right arm — keep it straight, angled slightly out from your body.",
      value: +input.rElbowAng.toFixed(0),
    });
  }

  // ── Arm raised (wrist above / near shoulder) ────────────────────────────
  const lSh = kp[L_SHOULDER];
  const rSh = kp[R_SHOULDER];
  const lWr = kp[L_WRIST];
  const rWr = kp[R_WRIST];
  const lHip = kp[L_HIP];
  const rHip = kp[R_HIP];
  const shY = (lSh[1] + rSh[1]) / 2;
  const hipY = (lHip[1] + rHip[1]) / 2;
  const trunkH = Math.abs(hipY - shY);

  let lWrRelSh = 0;
  let rWrRelSh = 0;
  if (trunkH > 0) {
    lWrRelSh = (lWr[1] - shY) / trunkH; // 0 = at shoulder, 1 = at hip
    rWrRelSh = (rWr[1] - shY) / trunkH;
    if (lWrRelSh < WRIST_REL_SHOULDER_RAISED_MAX) {
      errors.push({
        id: "arm_raised_left",
        severity: "error",
        label: "L arm raised",
        message: "Lower your left arm — let it hang at an angle, slightly out from your body.",
        value: +lWrRelSh.toFixed(2),
      });
    }
    if (rWrRelSh < WRIST_REL_SHOULDER_RAISED_MAX) {
      errors.push({
        id: "arm_raised_right",
        severity: "error",
        label: "R arm raised",
        message: "Lower your right arm — let it hang at an angle, slightly out from your body.",
        value: +rWrRelSh.toFixed(2),
      });
    }
    // Asymmetric arms — front view only (side view far arm occluded)
    if (view === "front" && Math.abs(lWrRelSh - rWrRelSh) > ARM_ASYMMETRY_MAX) {
      errors.push({
        id: "arms_asymmetric",
        severity: "warning",
        label: "Arms uneven",
        message: "Keep both arms at the same height, angled slightly out from your body.",
        value: +Math.abs(lWrRelSh - rWrRelSh).toFixed(2),
      });
    }
  }

  // ── Side-view: arm sticking outside body silhouette ──────────────────────
  // The near (right) arm should hang close alongside the trunk. If the wrist
  // or elbow drifts too far horizontally from the trunk midpoint, the arm is
  // protruding out of the silhouette and will corrupt body width measurements.
  // Thresholds mirror V2 armInsideBody in poseGate.generated.ts.
  if (view === "right") {
    const rShSide = kp[R_SHOULDER];
    const rHipSide = kp[R_HIP];
    const rElSide = kp[R_ELBOW];
    const rWrSide = kp[R_WRIST];
    const trunkLenSide = Math.hypot(
      rShSide[0] - rHipSide[0],
      rShSide[1] - rHipSide[1]
    );
    if (trunkLenSide > 1e-3) {
      const trunkXSide = (rShSide[0] + rHipSide[0]) / 2;
      const dxWrist = Math.abs(rWrSide[0] - trunkXSide) / trunkLenSide;
      const dxElbow = Math.abs(rElSide[0] - trunkXSide) / trunkLenSide;
      if (dxWrist > ARM_OUTSIDE_MAX_WRIST || dxElbow > ARM_OUTSIDE_MAX_ELBOW) {
        errors.push({
          id: "arm_outside_side",
          severity: "error",
          label: "Arm out from body",
          message: "Keep your arm close to your body — let it hang naturally by your side.",
          value: +Math.max(dxWrist, dxElbow).toFixed(3),
        });
      }
    }
  }

  // ── Leg stance (front view — side view legs are occluded) ───────────────
  if (view === "front") {
    const lAn = kp[L_ANKLE];
    const rAn = kp[R_ANKLE];
    const hipW = Math.abs(rHip[0] - lHip[0]);
    const ankleDx = Math.abs(rAn[0] - lAn[0]);
    const legRatio = hipW > 0 ? ankleDx / hipW : 0;
    if (legRatio < LEG_NARROW_MAX) {
      errors.push({
        id: "legs_too_close",
        severity: "warning",
        label: "Feet too close",
        message: "Step your feet about shoulder-width apart.",
        value: +legRatio.toFixed(2),
      });
    } else if (legRatio > LEG_WIDE_MAX) {
      errors.push({
        id: "legs_too_wide",
        severity: "warning",
        label: "Feet too wide",
        message: "Bring your feet closer together, roughly shoulder-width.",
        value: +legRatio.toFixed(2),
      });
    }
  }

  // ── Camera elevation (floor-camera detection) ───────────────────────────
  const cameraTooHighThreshold = view === "right" ? CAMERA_TOO_HIGH_M_SIDE : CAMERA_TOO_HIGH_M;
  const cameraTooLowThreshold  = view === "right" ? CAMERA_TOO_LOW_M_SIDE  : CAMERA_TOO_LOW_M;
  if (
    input.cameraElevationM !== undefined &&
    input.cameraElevationM !== null
  ) {
    if (input.cameraElevationM < cameraTooHighThreshold) {
      errors.push({
        id: "camera_too_high",
        severity: "error",
        label: "Camera too high",
        message: "Lower the phone — it should be around waist height, not above shoulder level.",
        value: +input.cameraElevationM.toFixed(3),
      });
    } else if (input.cameraElevationM > cameraTooLowThreshold) {
      errors.push({
        id: "camera_too_low",
        severity: "error",
        label: "Camera too low",
        message: "Raise the phone — it should be around waist height, not on the floor.",
        value: +input.cameraElevationM.toFixed(3),
      });
    }
  }

  return errors;
}

// ── Presentation helpers ─────────────────────────────────────────────────────

export const ERROR_CATEGORY_ORDER: PoseErrorId[] = [
  // Detection-failure categories (highest priority)
  "wrong_orientation",
  "bad_detection",
  "collapsed_detection",
  // Framing
  "clipped_top",
  "clipped_bottom",
  "clipped_side",
  "too_close",
  "too_far",
  "off_centre",
  // Pose
  "rotation",
  "torso_tilted",
  "arm_raised_left",
  "arm_raised_right",
  "arm_bent_left",
  "arm_bent_right",
  "arms_asymmetric",
  "arm_outside_side",
  "camera_too_high",
  "camera_too_low",
  "legs_too_close",
  "legs_too_wide",
];

// ── Voice message layer ──────────────────────────────────────────────────────
//
// Multiple detailed errors collapse into a single short, actionable voice
// prompt. Only ONE message is spoken at a time — the first match in priority
// order (highest priority first).

export type VoiceMessageId =
  | "wrong_orientation"   // phone held landscape
  | "not_detected"        // pose detection failed entirely
  | "step_back"           // head/feet clipped or too close — full body not in frame
  | "move_to_centre"      // side-clipped or off-centre
  | "adjust_camera"       // camera too high or too low
  | "face_camera"         // body rotated away
  | "stand_straight"      // torso tilted
  | "feet_apart"          // stance too narrow
  | "feet_together"       // stance too wide
  | "step_closer"         // too far from camera
  | "lower_arms"          // arms raised above shoulder
  | "straighten_arms"     // elbows bent
  | "arms_level"          // asymmetric arm height
  | "arms_in";            // arm protruding on side view

export interface VoiceMessage {
  id: VoiceMessageId;
  /** Short, spoken text sent to the user */
  text: string;
  /** Which detailed errors triggered this voice message */
  triggeredBy: PoseErrorId[];
}

interface VoiceMessageDef {
  id: VoiceMessageId;
  text: string;
  triggers: PoseErrorId[];
}

/** Ordered highest → lowest priority. First match wins. */
const VOICE_MESSAGE_DEFS: VoiceMessageDef[] = [
  // ── 1. Detection failures ──────────────────────────────────────────────────
  // Nothing else matters until the pose can be detected at all.
  {
    id: "wrong_orientation",
    text: "Turn your phone to portrait",
    triggers: ["wrong_orientation"],
  },
  {
    id: "not_detected",
    text: "Stand in clear light — I can't see your full body",
    triggers: ["bad_detection", "collapsed_detection"],
  },
  // ── 2. Body fully in frame ─────────────────────────────────────────────────
  // Framing must be right before any pose feedback makes sense.
  {
    id: "step_back",
    text: "Step back so your full body fits in frame",
    triggers: ["clipped_top", "clipped_bottom", "too_close"],
  },
  {
    id: "move_to_centre",
    text: "Move to the centre and keep your arms in frame",
    triggers: ["clipped_side", "off_centre"],
  },
  {
    id: "step_closer",
    text: "Step a little closer to the camera",
    triggers: ["too_far"],
  },
  // ── 3. Camera position (rare) ──────────────────────────────────────────────
  {
    id: "adjust_camera",
    text: "Place the phone at waist height",
    triggers: ["camera_too_high", "camera_too_low"],
  },
  // ── 4. Body rotation / alignment ──────────────────────────────────────────
  {
    id: "face_camera",
    text: "Face the camera directly",
    triggers: ["rotation"],
  },
  {
    id: "stand_straight",
    text: "Stand up straight",
    triggers: ["torso_tilted"],
  },
  // ── 5. Feet position ───────────────────────────────────────────────────────
  {
    id: "feet_apart",
    text: "Stand with feet shoulder-width apart",
    triggers: ["legs_too_close"],
  },
  {
    id: "feet_together",
    text: "Bring your feet a little closer together",
    triggers: ["legs_too_wide"],
  },
  // ── 6. Arm position ────────────────────────────────────────────────────────
  {
    id: "lower_arms",
    text: "Lower your arms",
    triggers: ["arm_raised_left", "arm_raised_right"],
  },
  {
    id: "straighten_arms",
    text: "Straighten your arms",
    triggers: ["arm_bent_left", "arm_bent_right"],
  },
  {
    id: "arms_level",
    text: "Keep both arms level, angled slightly out from your body",
    triggers: ["arms_asymmetric"],
  },
  {
    id: "arms_in",
    text: "Keep your arms close to your body",
    triggers: ["arm_outside_side"],
  },
];

/**
 * Returns the single highest-priority voice message for the given errors,
 * or null if there are no errors.
 */
export function getVoiceMessage(errors: PoseError[]): VoiceMessage | null {
  if (errors.length === 0) return null;
  const errorIds = new Set(errors.map((e) => e.id));
  for (const def of VOICE_MESSAGE_DEFS) {
    const triggered = def.triggers.filter((t) => errorIds.has(t));
    if (triggered.length > 0) {
      return { id: def.id, text: def.text, triggeredBy: triggered };
    }
  }
  return null;
}

export const VOICE_MESSAGE_ORDER: VoiceMessageId[] = VOICE_MESSAGE_DEFS.map((d) => d.id);

export const VOICE_MESSAGE_META: Record<VoiceMessageId, { title: string; description: string; triggers: PoseErrorId[] }> =
  Object.fromEntries(
    VOICE_MESSAGE_DEFS.map((d) => [d.id, {
      title: d.text,
      description: `Triggers: ${d.triggers.join(", ")}`,
      triggers: d.triggers,
    }])
  ) as Record<VoiceMessageId, { title: string; description: string; triggers: PoseErrorId[] }>;

// ── Presentation helpers ─────────────────────────────────────────────────────

export const ERROR_CATEGORY_META: Record<
  PoseErrorId,
  { title: string; description: string }
> = {
  wrong_orientation:    { title: "Wrong orientation",  description: "Image is landscape — must be portrait for body scanning" },
  bad_detection:        { title: "Detection failed",   description: "Body keypoints are anatomically impossible (inverted body or ankle above knee)" },
  collapsed_detection:  { title: "Body obscured",      description: "Left/right arm keypoints collapsed to same position — body not visible" },
  clipped_top:          { title: "Head clipped",       description: "Top of head cut off" },
  clipped_bottom:  { title: "Feet clipped",     description: "Feet cut off at bottom" },
  clipped_side:    { title: "Body cut sideways", description: "Arms or body too close to left/right edge — keypoint within 6% of image width from border" },
  too_close:       { title: "Too close",         description: "Body fills > 90% of frame" },
  too_far:         { title: "Too far",           description: "Body fills < 35% of frame" },
  off_centre:      { title: "Off centre",        description: "Body off centre by > 12% of image width" },
  rotation:        { title: "Rotated",           description: "Body rotated > 7.5° from facing camera" },
  torso_tilted:    { title: "Torso tilted",      description: "Torso leaning > 8° from vertical" },
  arm_raised_left: { title: "Left arm raised",   description: "Left wrist at or above shoulder" },
  arm_raised_right:{ title: "Right arm raised",  description: "Right wrist at or above shoulder" },
  arm_bent_left:   { title: "Left arm bent",     description: "Left elbow bent > 20°" },
  arm_bent_right:  { title: "Right arm bent",    description: "Right elbow bent > 20°" },
  arms_asymmetric: { title: "Arms uneven",       description: "One wrist much higher than the other" },
  legs_too_close:  { title: "Feet too close",    description: "Ankles narrower than hips" },
  legs_too_wide:   { title: "Feet too wide",     description: "Ankles wider than 2.5× hip width" },
  arm_outside_side: { title: "Arm out from body",  description: "Side-view: wrist or elbow too far from trunk midline — arm protruding out of silhouette" },
  camera_too_high:  { title: "Camera too high",   description: "Camera placed too high, looking down — shoulders much closer to camera than ankles (elev < −0.5 m)" },
  camera_too_low:   { title: "Camera too low",    description: "Camera placed on the floor, looking up — ankles much closer to camera than shoulders (elev > 0.15 m)" },
};
